import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { registrationsApi } from '../api/client';
import Logo from '../components/Logo';
import { getErrorMessage } from '../utils/errorHandler';

const TicketVerificationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [verifying, setVerifying] = useState(true);
  const [status, setStatus] = useState<'success' | 'already_verified' | 'error' | 'resent'>('success');
  const [errorMessage, setErrorMessage] = useState('');
  const [attendeeName, setAttendeeName] = useState('');

  useEffect(() => {
    if (token) {
      verifyEmail();
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await registrationsApi.verifyRegistration(token!);
      
      if (response.already_verified) {
        setStatus('already_verified');
      } else if (response.resent) {
        // Handle expired link with auto-resend
        setStatus('resent');
        setAttendeeName(response.attendee_name || '');
      } else {
        setStatus('success');
        setAttendeeName(response.attendee_name || '');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(getErrorMessage(error));
    } finally {
      setVerifying(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying your email...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        <div className="mb-6">
          <Logo size="medium" showCompanyName={true} />
        </div>
        {status === 'success' && (
          <>
            <CheckCircleIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-green-900 mb-2">Email Verified!</h1>
            <p className="text-gray-700 mb-4">
              Thank you for verifying your email, <strong>{attendeeName}</strong>.
            </p>
            <p className="text-gray-700 mb-6">
              Your ticket request has been added to the waitlist. You'll receive an email once your ticket is approved.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Go to Homepage
            </Link>
          </>
        )}

        {status === 'already_verified' && (
          <>
            <ExclamationCircleIcon className="h-16 w-16 text-yellow-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-yellow-900 mb-2">Already Verified</h1>
            <p className="text-gray-700 mb-6">
              This email address has already been verified. Your ticket request is in the system.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Go to Homepage
            </Link>
          </>
        )}

        {status === 'resent' && (
          <>
            <ExclamationCircleIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-blue-900 mb-2">New Verification Email Sent</h1>
            <p className="text-gray-700 mb-4">
              Hi <strong>{attendeeName}</strong>, your verification link had expired.
            </p>
            <p className="text-gray-700 mb-6">
              We've sent you a new verification email. Please check your inbox and click the new link to verify your registration.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> The new verification link will expire in 24 hours. If you don't receive the email within a few minutes, please check your spam folder.
              </p>
            </div>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Go to Homepage
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircleIcon className="h-16 w-16 text-red-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-red-900 mb-2">Verification Failed</h1>
            <p className="text-gray-700 mb-6">{errorMessage}</p>
            {errorMessage.includes('expired') && (
              <p className="text-gray-600 text-sm mb-6">
                Verification links expire after 24 hours. Please submit a new ticket request if you still wish to attend.
              </p>
            )}
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Go to Homepage
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default TicketVerificationPage;